import {
  Badge,
  Body1,
  Button,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { Diagnostics } from '@worklogix/shared';
import { useCallback, useEffect, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import { toErrorMessage } from './errors';

/**
 * Deploy readiness (K8). The question this answers is "did that deploy actually
 * work?", and it exists because neither health endpoint can answer it.
 *
 * `/health` proves the process is up and `/health/ready` proves it can reach the
 * database — but NEITHER can see a MISSING SECRET. The API resolves the kiosk PIN
 * pepper on every punch, so a build deployed before that secret exists breaks every
 * clock-in while `/health` still cheerfully answers 200: a perfectly healthy process
 * that nobody can clock in to.
 *
 * The endpoint existed before this page did, reachable only with a hand-crafted
 * bearer token — which made it useless to the person who most needs it right after a
 * deploy. Hence a page.
 *
 * It reports PRESENCE only, never a secret's value, and deliberately not its length
 * either: a length is a real hint about a credential and worth nothing to an operator.
 */

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
    // Title + action buttons cannot share one line on a phone. Without wrapping,
    // this row is wider than the screen and — being a non-scrolling child — it
    // drags the whole content region sideways, clipping everything's left edge
    // (Michael, 2026-09-10: "Employees" rendered as "ees").
    flexWrap: 'wrap',
  },
  /**
   * The grid is its OWN scroller. Three tracks with nowrap labels cannot fit a
   * phone, and an overflowing child escapes to the document (an ancestor's
   * maxWidth:100% caps the ancestor, it does not clip the child) — which is how
   * the whole page ended up scrolled sideways. `overflow-x` on the grid itself
   * needs no extra wrapper element.
   */
  grid: {
    display: 'grid',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...SHRINKABLE,
    gridTemplateColumns: 'minmax(180px, max-content) max-content 1fr',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    alignItems: 'center',
  },
  label: { fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap' },
  note: { color: tokens.colorNeutralForeground3 },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  mono: { fontFamily: tokens.fontFamilyMonospace },
});

/** A check's outcome plus WHY it matters when it fails. */
interface CheckRow {
  label: string;
  ok: boolean;
  /** Shown only on failure — what the operator should actually do about it. */
  consequence: string;
}

function checkRows(d: Diagnostics): CheckRow[] {
  return [
    {
      label: 'Database',
      ok: d.checks.database === 'up',
      consequence: 'The API cannot reach Postgres. Nothing will work; check the server and firewall.',
    },
    {
      label: 'Kiosk token secret',
      ok: d.checks.kioskJwtSecret === 'resolvable',
      consequence: 'Kiosks cannot be authenticated — every punch returns 401 and devices unpair.',
    },
    {
      label: 'Kiosk PIN pepper',
      ok: d.checks.kioskPinPepper === 'resolvable',
      consequence:
        'EVERY clock-in will fail: the API needs this to resolve a PIN. Create kiosk-pin-pepper in Key Vault before anyone punches. It is create-once — never overwrite an existing one.',
    },
  ];
}

export function DiagnosticsTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [data, setData] = useState<Diagnostics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getDiagnostics());
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = data === null ? [] : checkRows(data);
  const failing = rows.filter((r) => !r.ok);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Title3>Deploy readiness</Title3>
        <Button onClick={() => void load()} disabled={loading}>
          {loading ? 'Checking…' : 'Re-check'}
        </Button>
      </div>

      <Body1 className={styles.note}>
        Run this straight after a deploy. It reports whether the configuration this build
        needs is actually present — which the health endpoints cannot tell you, because a
        process with a missing secret is still a perfectly healthy process.
      </Body1>

      {error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {loading && data === null ? <Spinner label="Checking configuration…" /> : null}

      {data !== null && (
        <>
          {failing.length === 0 ? (
            <MessageBar intent="success">
              <MessageBarBody>
                All checks pass — this build has the configuration it needs.
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="error">
              <MessageBarBody>
                {failing.length === 1
                  ? '1 check failed. See below before anyone relies on the kiosks.'
                  : `${String(failing.length)} checks failed. See below before anyone relies on the kiosks.`}
              </MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.section}>
            <div className={styles.grid}>
              {rows.map((row) => (
                <Row key={row.label} row={row} />
              ))}
            </div>
          </div>

          <div className={styles.section}>
            <Text weight="semibold">Policy in effect</Text>
            <Body1 className={styles.note}>
              Read back from the running process, so a setting that never reached the
              container shows up here rather than staying invisible.
            </Body1>
            <div className={styles.grid}>
              <span className={styles.label}>Minimum PIN length</span>
              <span className={styles.mono}>{data.policy.pinMinDigits} digits</span>
              <span />
              <span className={styles.label}>Punch rate limit</span>
              <span className={styles.mono}>
                {data.policy.throttlePunchLimit} per{' '}
                {Math.round(data.policy.throttleWindowMs / 1000)}s per kiosk
              </span>
              <span />
              <span className={styles.label}>Build</span>
              <span className={styles.mono}>
                {data.buildId === '' ? '(not set)' : data.buildId}
              </span>
              <span />
            </div>
          </div>

          <Text size={200} className={styles.note}>
            Checked {new Date(data.timestamp).toLocaleString()}
          </Text>
        </>
      )}
    </div>
  );
}

/** One check: name, pass/fail, and on failure what it means. */
function Row({ row }: { row: CheckRow }): JSX.Element {
  const styles = useStyles();
  return (
    <>
      <span className={styles.label}>{row.label}</span>
      <Badge appearance="filled" color={row.ok ? 'success' : 'danger'}>
        {row.ok ? 'OK' : 'FAILED'}
      </Badge>
      {/* The consequence is shown only when it applies — a wall of "what if this broke"
          next to passing checks trains people to skip the whole page. */}
      <Text size={200} className={styles.note}>
        {row.ok ? '' : row.consequence}
      </Text>
    </>
  );
}
